import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getDeliveryCanTransaction, updateDeliveryCanTransaction } from '../../actions/deliveryCanTransaction';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Button, 
    Badge, 
    Alert, 
    Spinner,
    Modal,
    Form,
    Table
} from 'react-bootstrap';
import { 
    ArrowLeft, 
    Package, 
    User, 
    MapPin, 
    Clock, 
    CheckCircle, 
    AlertCircle, 
    Phone,
    Calendar,
    FileText,
    Truck,
    XCircle,
    AlertTriangle,
    Edit,
    Eye,
    ExternalLink
} from 'lucide-react';
import "bootstrap/dist/css/bootstrap.min.css";

const DeliveryCanTransaction = ({
    transaction,
    loading,
    error,
    getDeliveryCanTransaction,
    updateDeliveryCanTransaction
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [statusReason, setStatusReason] = useState('');
    const [deliveryOtp, setDeliveryOtp] = useState('');
    const [otpError, setOtpError] = useState('');

    useEffect(() => {
        document.title = 'Delivery Transaction Details';
        if (id) {
            getDeliveryCanTransaction(id);
        }
    }, [getDeliveryCanTransaction, id]);

    const formatDate = (dateString) => {
        if (!dateString) return 'Not Available';
        return new Date(dateString).toLocaleString();
    };

    const formatPhone = (phone) => {
        if (phone && phone.length === 10) {
            return `+91 ${phone}`;
        }
        return phone || 'Not Available';
    };

    const getStatusBadge = (status) => {
        switch(status?.toLowerCase()) {
            case 'pending':
                return <Badge bg="warning" className="fs-6">Pending</Badge>;
            case 'assigned':
                return <Badge bg="info" className="fs-6">Assigned</Badge>;
            case 'in-progress':
                return <Badge bg="primary" className="fs-6">In Progress</Badge>;
            case 'scheduled':
                return <Badge bg="dark" className="fs-6">Scheduled</Badge>;
            case 'delivered':
                return <Badge bg="success" className="fs-6">Delivered</Badge>;
            case 'cancelled':
                return <Badge bg="secondary" className="fs-6">Cancelled</Badge>;
            case 'failed':
                return <Badge bg="danger" className="fs-6">Failed</Badge>;
            default:
                return <Badge bg="light" text="dark" className="fs-6">{status || 'Unknown'}</Badge>;
        }
    };

    const closeStatusModal = () => {
        setShowStatusModal(false);
        setNewStatus('');
        setStatusReason('');
        setDeliveryOtp('');
        setOtpError('');
    };

    const handleStatusUpdate = async () => {
        if (!newStatus || !statusReason.trim()) return;
        if (newStatus === 'delivered' && deliveryOtp.trim() !== String(transaction.staticOtp || '')) {
            setOtpError('Invalid delivery OTP');
            return;
        }
        await updateDeliveryCanTransaction(id, newStatus, { comment: statusReason });
        closeStatusModal();
        getDeliveryCanTransaction(id);
    };



    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading transaction details...</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-4">
                <Alert variant="danger">
                    <AlertCircle className="me-2" />
                    {error}
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate(-1)}>
                    <ArrowLeft className="me-2" />
                    Go Back
                </Button>
            </Container>
        );
    }

    if (!transaction) {
        return (
            <Container className="py-4">
                <Alert variant="info">
                    <AlertCircle className="me-2" />
                    Transaction not found.
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate(-1)}>
                    <ArrowLeft className="me-2" />
                    Go Back
                </Button>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button
                        variant="outline-primary"
                        onClick={() => navigate('/inventory/v1.2/delivery-transactions')}
                        className="mb-2"
                    >
                        <ArrowLeft className="me-2" />
                        Back to Transactions
                    </Button>
                    <h2 className="text-primary mb-0">
                        <FileText className="me-2" />
                        Delivery Transaction Details
                    </h2>
                </div>
                <div className="d-flex gap-2">
                    {transaction.status !== 'delivered' && transaction.status !== 'cancelled' && (
                        <Button
                            variant="outline-primary"
                            onClick={() => setShowStatusModal(true)}
                        >
                            <Edit className="me-2" />
                            Update Status
                        </Button>
                    )}
                </div>
            </div>

            <Row className="g-4">
                {/* Transaction Overview */}
                <Col lg={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-primary text-white">
                            <h5 className="mb-0">
                                <FileText className="me-2" />
                                Transaction Overview
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table borderless className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-bold text-muted">Transaction ID:</td>
                                        <td className="fw-bold text-primary">{transaction.transactionId}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Status:</td>
                                        <td>{getStatusBadge(transaction.status)}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Request Time:</td>
                                        <td>
                                            <Calendar className="me-1" size={14} />
                                            {formatDate(transaction.requestTime)}
                                        </td>
                                    </tr>
                                    {transaction.assignedTime && (
                                        <tr>
                                            <td className="fw-bold text-muted">Assigned Time:</td>
                                            <td>
                                                <Calendar className="me-1" size={14} />
                                                {formatDate(transaction.assignedTime)}
                                            </td>
                                        </tr>
                                    )}
                                    {transaction.scheduledFor && (
                                        <tr>
                                            <td className="fw-bold text-muted">Scheduled For:</td>
                                            <td>
                                                <Calendar className="me-1" size={14} />
                                                {formatDate(transaction.scheduledFor)}
                                            </td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td className="fw-bold text-muted">Deposit Used:</td>
                                        <td>
                                            <Badge bg={transaction.depositUsed ? 'success' : 'secondary'}>
                                                {transaction.depositUsed ? 'Yes' : 'No'}
                                            </Badge>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Customer Information */}
                <Col lg={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <User className="me-2" />
                                Customer Information
                            </h5>
                            {transaction.user?._id && (
                                <Link
                                    to={`/inventory/v1.2/users/${transaction.user._id}`}
                                    className="btn btn-light btn-sm"
                                >
                                    <Eye className="me-1" size={14} />
                                    View Customer
                                </Link>
                            )}
                        </Card.Header>
                        <Card.Body>
                            <Table borderless className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-bold text-muted">Name:</td>
                                        <td className="fw-bold">{transaction.user?.name || 'Deleted User'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Phone:</td>
                                        <td>
                                            <Phone className="me-1" size={14} />
                                            {formatPhone(transaction.user?.phone)}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Product Information */}
                <Col lg={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-success text-white">
                            <h5 className="mb-0">
                                <Package className="me-2" />
                                Product Information
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table borderless className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-bold text-muted">Product Name:</td>
                                        <td className="fw-bold">{transaction.product?.name || 'Deleted Product'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Brand:</td>
                                        <td>{transaction.product?.brand || 'Unknown Brand'}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Can Details */}
                <Col lg={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-warning text-dark">
                            <h5 className="mb-0">
                                <Package className="me-2" />
                                Can Details
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table borderless className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-bold text-muted">Filled Cans:</td>
                                        <td>
                                            <Badge bg="success" className="fs-6">{transaction.filledCans}</Badge>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Empty Cans:</td>
                                        <td>
                                            <Badge bg="secondary" className="fs-6">{transaction.emptyCans}</Badge>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Damage Cans:</td>
                                        <td>
                                            <Badge bg={transaction.damageCans > 0 ? 'danger' : 'light'} 
                                                  text={transaction.damageCans > 0 ? 'white' : 'dark'} 
                                                  className="fs-6">
                                                {transaction.damageCans}
                                            </Badge>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Total Cans:</td>
                                        <td>
                                            <Badge bg="primary" className="fs-6">
                                                {transaction.filledCans + transaction.emptyCans + transaction.damageCans}
                                            </Badge>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Delivery Address Information */}
                {transaction.deliveryAddress && (
                    <Col lg={12}>
                        <Card>
                            <Card.Header className="bg-danger text-white">
                                <h5 className="mb-0">
                                    <MapPin className="me-2" />
                                    Delivery Address Information
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={8}>
                                        <Table borderless className="mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="fw-bold text-muted">Full Address:</td>
                                                    <td className="fw-bold">{transaction.deliveryAddress.fullAddress}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold text-muted">Flat Number:</td>
                                                    <td>{transaction.deliveryAddress.flatNumber}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold text-muted">Building Name:</td>
                                                    <td>{transaction.deliveryAddress.buildingName}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold text-muted">Landmark:</td>
                                                    <td>{transaction.deliveryAddress.landmark}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Col>
                                    {transaction.deliveryLocation && transaction.deliveryLocation.coordinates && (
                                        <Col md={4}>
                                            <h6 className="text-muted">GPS Coordinates</h6>
                                            <Table borderless className="mb-0">
                                                <tbody>
                                                    <tr>
                                                        <td className="fw-bold text-muted">Latitude:</td>
                                                        <td className="font-monospace">{transaction.deliveryLocation.coordinates[0]}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="fw-bold text-muted">Longitude:</td>
                                                        <td className="font-monospace">{transaction.deliveryLocation.coordinates[1]}</td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={2}>
                                                            <a 
                                                                href={`https://www.google.com/maps/dir/?api=1&destination=${transaction.deliveryLocation.coordinates[0]},${transaction.deliveryLocation.coordinates[1]}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-outline-primary btn-sm"
                                                            >
                                                                <MapPin size={14} className="me-1" />
                                                                Open in Maps
                                                            </a>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                {/* Agent & Sector Information */}
                {(transaction.agent || transaction.sector) && (
                    <Col lg={12}>
                        <Card>
                            <Card.Header className="bg-dark text-white">
                                <h5 className="mb-0">
                                    <Truck className="me-2" />
                                    Assignment Information
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {transaction.agent && (
                                        <Col md={6}>
                                            <h6 className="text-muted">Assigned Agent</h6>
                                            <Table borderless className="mb-0">
                                                <tbody>
                                                    <tr>
                                                        <td className="fw-bold text-muted">Name:</td>
                                                        <td className="fw-bold">{transaction.agent.name}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Col>
                                    )}
                                    {transaction.sector && (
                                        <Col md={6}>
                                            <h6 className="text-muted">Sector Information</h6>
                                            <Table borderless className="mb-0">
                                                <tbody>
                                                    <tr>
                                                        <td className="fw-bold text-muted">Sector:</td>
                                                        <td className="fw-bold">{transaction.sector.name}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* Status Update Modal */}
            <Modal show={showStatusModal} onHide={closeStatusModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Edit className="me-2" />
                        Update Transaction Status
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">New Status</Form.Label>
                        <Form.Select
                            value={newStatus}
                            onChange={(e) => {
                                setNewStatus(e.target.value);
                                setDeliveryOtp('');
                                setOtpError('');
                            }}
                        >
                            <option value="">Select Status</option>
                            {transaction.status === 'pending' && <option value="assigned">Assigned</option>}
                            {['pending', 'assigned'].includes(transaction.status) && <option value="in-progress">In Progress</option>}
                            {['assigned', 'in-progress'].includes(transaction.status) && <option value="delivered">Delivered</option>}
                            {['pending', 'assigned', 'in-progress'].includes(transaction.status) && <option value="failed">Failed</option>}
                            {['pending', 'assigned', 'in-progress'].includes(transaction.status) && <option value="cancelled">Cancelled</option>}
                        </Form.Select>
                    </Form.Group>
                    {newStatus === 'delivered' && (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Delivery OTP</Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                value={deliveryOtp}
                                onChange={(e) => {
                                    setDeliveryOtp(e.target.value);
                                    setOtpError('');
                                }}
                                isInvalid={!!otpError}
                                placeholder="Ask the customer for the OTP"
                            />
                            <Form.Control.Feedback type="invalid">{otpError}</Form.Control.Feedback>
                        </Form.Group>
                    )}
                    <Form.Group>
                        <Form.Label className="fw-bold">Reason/Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={statusReason}
                            onChange={(e) => setStatusReason(e.target.value)}
                            placeholder="Provide reason for status change..."
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeStatusModal}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleStatusUpdate}
                        disabled={!newStatus || !statusReason.trim() || (newStatus === 'delivered' && !deliveryOtp.trim())}
                    >
                        Update Status
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

DeliveryCanTransaction.propTypes = {
    transaction: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    getDeliveryCanTransaction: PropTypes.func.isRequired,
    updateDeliveryCanTransaction: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    transaction: state.deliveryCanTransaction.transaction,
    loading: state.deliveryCanTransaction.loading,
    error: state.deliveryCanTransaction.error
});

export default connect(mapStateToProps, { 
    getDeliveryCanTransaction, 
    updateDeliveryCanTransaction 
})(DeliveryCanTransaction);
